import { Track, TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { datadogRum } from '@sunbit-dev/common/services/datadog';
import { checkIfKnownError } from '@sunbit-dev/purchase-common/src/helpers/logs';
import mixpanel from 'mixpanel-browser';
import { logsService } from '~/services/LogsService';
import { generateString } from '@sunbit-dev/purchase-common/src/utilities/string';
import { useCallback } from 'react';
import { sampling } from '@sunbit-dev/common/services/sampling';
import { launchDarklyClient } from '~/helpers/launchDarkly';

export const isTrackingActive = window.sunbitEnv !== 'automation';
export const NONE_SAMPLED_ENVS = ['local', 'remote-dev'];

const updateIdentifier = ({ identifier }: { identifier: string }) => {
  mixpanel.identify(identifier);
};

export const useTrack = () => {
  const track = useCallback(({ eventName, type, props, datadogInfo = { type: TrackCategory.Action } }: Track) => {
    const _type = type || datadogInfo.type;

    if (!isTrackingActive) return;

    const fullPayload = { ...logsService.info, ...props };

    if (_type === TrackCategory.Sample && !NONE_SAMPLED_ENVS.includes(window.sunbitEnv)) {
      const { experimentInfo, ...rest } = fullPayload;
      return sampling?.event(eventName, {
        ...rest,
        customerId: rest.customerId || logsService.info.customer.id,
      });
    }

    // Always logging to mixpanel
    mixpanel.track(eventName, fullPayload);
    if (type === TrackCategory.View) {
      launchDarklyClient.trackEvent('loan_application_fe_' + eventName);
    }
    const errorCode = datadogInfo?.errorInfo?.code;
    const isKnownError = checkIfKnownError(errorCode);

    if (_type === TrackCategory.View) {
      return datadogRum.startView(eventName);
    }
    if (isKnownError) {
      return datadogRum.addAction(eventName || 'General', {
        ...props,
        isKnownError: true,
      });
    } else if (_type === TrackCategory.Action) {
      return datadogRum.addAction(eventName || 'General', props);
    } else if (_type === TrackCategory.Error) {
      datadogRum.addRumGlobalContext(`error-${eventName}-${generateString()}`, props);
      datadogRum.addError(new Error(eventName || 'General'), props);
    }
  }, []);

  return { track, updateIdentifier };
};
